/* eslint-disable no-param-reassign */
import { useBeforeUnload, useNavigate } from "react-router-dom";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, Checkbox, SxProps, TableRow, Typography, TextField, Tooltip, SpeedDial } from "@mui/material";
import { SuccessAlertContext } from "App";
import HelpIcon from "@mui/icons-material/Help";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { checkAccessTokenExpired, getUserType } from "util/auth";
import ERROR_MESSAGE from "constants/message";
import Notify from "config/notify";
import { SLASH_FORMAT_DATE } from "constants/format";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  AmountSymbol,
  StorehouseSupplierOptions,
  SupplierSearchApiParams,
  SupplierStock,
  SupplierStockSet,
  SupplierStockSingle,
  SupplierStockSingleInSet,
} from "../types";
import useSupplierStockList from "../hooks/useSupplierStockList";
import useStockSearch from "../hooks/useStockSearch";
import StockContainer from "../components/StockContainer";
import StockSearchDialog from "../components/StockSearchDialog";
import StockTable from "../components/StockTable";
import StockUpdateConfirmDialog from "../components/StockUpdateConfirmDialog";
import {
  amountSymbols,
  amountSymbolsWithBlank,
  INT_MAX,
  stockVisibleTypeLabel,
  storehouseSupplierOptions,
} from "../constants";
import TableCellSmall, {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "../components/TableCellSmall";
import { calcSymbol, dispSymbol, removeEmptyValues } from "../utils/common";
import CsvModals from "../components/CsvModals";
import updateStockApi from "../api/updateStockApi";
import CustomTooltip from "../components/CustomTooltip";
import { SelectForm } from "../components/Common";

const limit = 10;

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  supplierBottomCol: {
    textAlign: "center",
    p: 0.5,
    top: 58,
    // top: 33,
  },
};

/**
 * 仕入先・外部倉庫在庫一覧画面（仕入先、外部倉庫が自社の在庫のみ閲覧、更新する）
 * @returns JSX.Element
 */
export default function SupplierStockList() {
  const isExistCd = localStorage.getItem("supplier_cd") || localStorage.getItem("warehouse_cd");
  const navigate = useNavigate();
  const isSupplier = !!localStorage.getItem("supplier_cd");
  const { showAlert } = useContext(SuccessAlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [
    {
      total,
      stockList,
      isLoading: isStockLoading,
      isMoreLoading: isMoreStockLoading,
      changedStockList,
      isFastLoading,
      fireMoreLoadEvent,
    },
    {
      setStockList,
      get: getStockList,
      getMore: getMoreStockList,
      updatedStockList,
      setFireMoreLoadEvent,
      setInit,
      setChangedStockList,
    },
  ] = useSupplierStockList(showAlert);
  const [isOpenStockSearch, setIsOpenStockSearch] = useState(false);
  const [isOpenUpdateConfirmDialog, setIsOpenUpdateConfirmDialog] = useState(false);
  const [storehouseSupplier] = useState<StorehouseSupplierOptions>(isSupplier ? '仕入先' : '倉庫');
  const [isReadonly, setIsReadonly] = useState(false);
  const [isUpdateProcessing, setIsUpdateProcessing] = useState(false);
  const [isOpenStockCsvUpload, setIsOpenStockCsvUpload] = useState(false);
  const [isOpenThresholdCsvUpload, setIsOpenThresholdCsvUpload] = useState(false);
  const [supplierCd] = useState(
    (isSupplier ? localStorage.getItem("supplier_cd") : localStorage.getItem("warehouse_cd")) ?? "",
  );
  const csvDownloading = useRef(false);

  // 仕入先複数在庫表示対応
  const [selectedRegionWarehouseIndex, setSelectedRegionWarehouseIndex] = useState<number>(0); // 地域倉庫のindex(supplierWarehouseのindexではなく!isCenterでfilterされたindex)
  const [selectedCenterWarehouseIndex, setSelectedCenterWarehouseIndex] = useState<number>(0); // センターのindex(supplierWarehouseのindexではなくisCenterでfilterされたindex)

  const [
    {
      productCd,
      productName,
      brand,
      JANCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
    },
    {
      setProductCd,
      setProductName,
      setBrand,
      setJANCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setCategory,
    },
    { classificationOptions, tyreOptions, wheelOptions, brandOptions },
    { isLoading: isOptionsLoading },
  ] = useStockSearch(false, "Storehouse", "");

  const [isScrollTop, setIsScrollTop] = useState(false);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 現在の検索条件にあるデータを基にAPIのパラメータを作成
  const createGetListApiParams = (): SupplierSearchApiParams => {
    const params: SupplierSearchApiParams = {
      code: supplierCd,
      item_cd: productCd,
      item_name: productName,
      brand: brand ?? "",
      jan_cd: JANCd,
      item_num: productNumber,
      tire_width: classification === "タイヤ" ? tyreWidth : undefined,
      tire_ratio: classification === "タイヤ" ? tyreRatio : undefined,
      tire_rim_size: classification === "タイヤ" ? tyreRimDiam : undefined,
      wheel_width: classification === "ホイール" ? wheelWidth : undefined,
      wheel_pcd: classification === "ホイール" ? wheelPcd : undefined,
      wheel_hole: classification === "ホイール" ? wheelHole : undefined,
      wheel_rim_size: classification === "ホイール" ? wheelRimDiam : undefined,
      class_cd: classificationOptions.some((co) => co.name === classification)
        ? classificationOptions.filter((co) => co.name === classification)[0].id
        : "",
    };
    return removeEmptyValues(params);
  };

  // 2回目以降getStockListを呼び出す場合、更新状態をリセットする。
  const getStockListUpdate = async () => {
    setChangedStockList([]);
    setSelectedRegionWarehouseIndex(0);
    setSelectedCenterWarehouseIndex(0);
    // 仕入先の場合は仕入先コードをfillするために取得
    await getStockList(createGetListApiParams(), isSupplier);
  };

  const onSearchClick = async () => {
    setIsOpenStockSearch(false);
    await getStockListUpdate();
  };

  // 単品かセットか
  const checkProductSet = useCallback(
    (stock: SupplierStock): stock is SupplierStockSet => "productSetCd" in stock,
    [],
  );

  // 単品であることは前提で、プルダウンを押したときに表示される、セットに付随する単品かどうか
  const checkProductSingleInSet = useCallback(
    (stock: SupplierStockSingle | SupplierStockSingleInSet): stock is SupplierStockSingleInSet =>
      "quantity" in stock,
    [],
  );

  const handleToggleOpenRow = useCallback(
    (stockSetId: string, flag: boolean) => {
      setStockList(() =>
        stockList.map((s) => {
          if (checkProductSet(s) && s.id === stockSetId) {
            s.show = flag;
          }
          return s;
        }),
      );
    },
    [checkProductSet, stockList],
  );

  const dispSymbolReadonly = useCallback(
    (stock: SupplierStockSingle) =>
      stock.amountCount, // 外部倉庫は常に数値表示
      // dispSymbol(stock.amountCount, stock.stockVisibleType, stock.symbolThresholdList),
    [],
  );

  const onChangeAmount = useCallback(
    (value: string, index: number) => {
      let newValue: number | AmountSymbol | undefined;
      const newStockList = [...stockList];
      if (value === amountSymbolsWithBlank[0]) {
        newValue = undefined;
        newStockList[index].amountSymbol = newValue;
        if (newStockList[index].amountCount === undefined) {
          newStockList[index].amountCount = 0;
        }
      } else if (typeof Number(value) === "number" && !Number.isNaN(Number(value))) {
        newValue = Number(value);
        if (newValue > 10 ** 20) {
          return;
        }
        const st = stockList[index].symbolThresholdList;
        if (storehouseSupplier === "仕入先" && st) {
          newStockList[index].amountSymbol = calcSymbol(newValue, st);
        }
        newStockList[index].amountCount = newValue;
      } else if (
        typeof value === "string" &&
        (amountSymbols as readonly string[]).includes(value)
      ) {
        newValue = value as AmountSymbol;
        newStockList[index].amountSymbol = newValue;
      } else {
        return;
      }
      setStockList(newStockList);
      updatedStockList();
    },
    [stockList, storehouseSupplier, updatedStockList],
  );

  // 更新確認画面を開く
  const onUpdateConfirm = () => {
    setIsOpenUpdateConfirmDialog(true);
    updatedStockList();
  };

  // 確認ダイアログで更新ボタンを押下した時
  const onUpdateAmount = async () => {
    const sList = updatedStockList();
    setIsUpdateProcessing(true);
    let successFlag = true;
    try {
      // ToDo: 更新の引数を決める必要あり
      await updateStockApi({ stockList: sList, isSupplier });
      showAlert("在庫数量を更新しました。");
      setIsOpenUpdateConfirmDialog(false);
    } catch (err) {
      console.error(err);
      successFlag = false;
      showAlert("更新に失敗しました。", "error");
      setIsOpenUpdateConfirmDialog(false);
    } finally {
      setIsUpdateProcessing(false);
    }

    // 取得し直し
    if (successFlag) {
      await getStockListUpdate();
    }
  };

  const onChangeReadonlyScreen = () => {
    setIsReadonly((prev) => !prev);
  };

  const handleBack = () => navigate("/");

  // 更新しないままサイトを離れた時に警告する
  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (changedStockList.length && !csvDownloading.current) e.preventDefault();
      },
      [changedStockList],
    ),
    { capture: true },
  );

  // 地域倉庫があるかどうか
  const checkExistRegionWarehouse = () => !!(stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter).length > 0);

  // センター倉庫があるかどうか
  const checkExistCenterWarehouse = () => !!(stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => sw.isCenter).length > 0);

  const onChangeArrival = useCallback(
    (s: string, index: number) => {
      const newStockList = [...stockList];
      newStockList[index].arrivalDate = s;
      setStockList(newStockList);
      updatedStockList();
    },
    [updatedStockList, stockList],
  );

  // 外部倉庫数量変更セル
  const editableAmountCountCell = useCallback((stock: SupplierStockSingle, index: number) => (
    <TextField type="Number" size="small" value={stock.amountCount} onChange={
        (e) => {
          if (Number(e.target.value) > INT_MAX) {
            return;
          }
          onChangeAmount(e.target.value, index)
        }
      }
      inputProps={{
        sx: {fontSize: 12, textAlign: 'right'},
        min: 0,
        max: INT_MAX
      }}
      onKeyDown={(e) => {if (e.key === '-' || e.key === '.') e.preventDefault()}}
    />
  ), [onChangeAmount]);

  // 仕入先地域、センター倉庫数量変更
  const onChangeWarehouseAmount = useCallback((value: string, index: number, warehouseIndex: number) => {
    setStockList((prev) => {
      const newStockList = [...prev];
      const w = newStockList[index].supplierWarehouses;
      if(w) w[warehouseIndex].amount = Number(value);
      return newStockList;
    });
    updatedStockList();
  }, [updatedStockList]);

  const editableWarehouseAmountCell = useCallback((sw: NonNullable<SupplierStockSingle["supplierWarehouses"]>[number], index: number, warehouseIndex: number) => (
    <TextField type="Number" size="small" value={sw.amount} onChange={
        (e) => {
          if (Number(e.target.value) > INT_MAX) {
            return;
          }
          onChangeWarehouseAmount(e.target.value, index, warehouseIndex)
        }
      }
      inputProps={{
        sx: {fontSize: 12, textAlign: 'right'},
        min: 0,
        max: INT_MAX
      }}
      onKeyDown={(e) => {if (e.key === '-' || e.key === '.') e.preventDefault()}}
    />
  ), [onChangeWarehouseAmount]);

  const editableArrivalDateCell = useCallback(
    (stock: SupplierStockSingle, index: number) => (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
        <DatePicker
          // label="入荷予定日"
          format={SLASH_FORMAT_DATE}
          value={stock.arrivalDate ? dayjs(stock.arrivalDate, SLASH_FORMAT_DATE) : null}
          onChange={(val) => onChangeArrival(val ? val.format(SLASH_FORMAT_DATE) : "", index)}
          slotProps={{ textField: { size: "small" } }}
          sx={{
            "& .MuiInputBase-input.MuiOutlinedInput-input": { fontSize: 12, minWidth: 90 },
          }}
        />
      </LocalizationProvider>
    ),
    [onChangeArrival],
  );

  const readOnlyTBodyRender = useCallback(
    (stock: SupplierStockSingle | SupplierStockSingleInSet) => (
      <>
        {/* 仕入先のセンター倉庫ユーザー表示 */}
        { isSupplier && stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => sw.isCenter).length > 0 &&
          <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => sw.isCenter)[selectedCenterWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
        }
        {/* 仕入先の地域倉庫ユーザー表示 */}
        { isSupplier && stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => !sw.isCenter).length > 0 &&
          <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => !sw.isCenter)[selectedRegionWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
        }
        { !isSupplier &&
          <TableCellNarrow sx={{ minWidth: 40 }}>
            <Box textAlign={typeof dispSymbolReadonly(stock) === "number" ? "right" : "center"}>
              {dispSymbolReadonly(stock).toLocaleString()}
            </Box>
          </TableCellNarrow>
        }
        <TableCellSmall>{stock.arrivalDate}</TableCellSmall>
        {isSupplier && (
          <>
            <TableCellSmall>
              <Box textAlign="right">
                {stock.symbolThresholdList &&
                  `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[0].toLocaleString() : "-"}`}
              </Box>
            </TableCellSmall>
            <TableCellSmall>
              <Box textAlign="right">
                {stock.symbolThresholdList &&
                  `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[stock.symbolThresholdList.length - 1].toLocaleString() : "-"}`}
              </Box>
            </TableCellSmall>
            <TableCellSmall>{stockVisibleTypeLabel[stock.stockVisibleType] ?? '-'}</TableCellSmall>
          </>
        )}
      </>
    ),
    [dispSymbolReadonly, isSupplier, selectedCenterWarehouseIndex, selectedRegionWarehouseIndex],
  );

  const editableTBodyRender = useCallback(
    (stock: SupplierStockSingle | SupplierStockSingleInSet, index?: number) => (
        <>
          {isSupplier && (
            <>
              {/* 仕入先のセンター倉庫ユーザー表示 */}
              {stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => sw.isCenter).length > 0 &&
                <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => sw.isCenter)[selectedCenterWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
              }
              {/* 仕入先の地域倉庫ユーザー表示 */}
              {stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => !sw.isCenter).length > 0 &&
                <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => !sw.isCenter)[selectedRegionWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
              }
              {/* 仕入先のセンター倉庫在庫数量入力 */}
              {stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => sw.isCenter).length > 0 && (
                <TableCellWide>
                  { index === undefined
                    ?
                      stock.amountCount
                    :
                      editableWarehouseAmountCell(stock.supplierWarehouses.filter(
                        sw => sw.isCenter)[selectedCenterWarehouseIndex],
                        index,
                        stock.supplierWarehouses.reduce<number[]>((acc, cur, idx) => {
                          if (cur.isCenter) acc.push(idx); // 地域倉庫のみindexを抽出
                          return acc;
                        }, [])[selectedCenterWarehouseIndex]
                      )
                  }
                </TableCellWide>
              )}
              {/* 仕入先の地域倉庫在庫数量入力 */}
              {stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => !sw.isCenter).length > 0 && (
                <TableCellWide>
                  { index === undefined
                    ?
                      stock.amountCount
                    :
                      editableWarehouseAmountCell(stock.supplierWarehouses.filter(
                        sw => !sw.isCenter)[selectedRegionWarehouseIndex],
                        index,
                        stock.supplierWarehouses.reduce<number[]>((acc, cur, idx) => {
                          if (!cur.isCenter) acc.push(idx); // 地域倉庫のみindexを抽出
                          return acc;
                        }, [])[selectedRegionWarehouseIndex]
                      )
                  }
                </TableCellWide>
              )}
            </>
          )}
          { !isSupplier &&
            <TableCellWide>
              {index === undefined ? stock.amountCount : editableAmountCountCell(stock, index)}
            </TableCellWide>
          }
          <TableCellWide>
            {index === undefined ? stock.arrivalDate : editableArrivalDateCell(stock, index)}
          </TableCellWide>
          {isSupplier && (
            <>
              <TableCellSmall>
                <Box textAlign="right">
                  {stock.symbolThresholdList &&
                    `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[0].toLocaleString() : "-"}`}
                </Box>
              </TableCellSmall>
              <TableCellSmall>
                <Box textAlign="right">
                  {stock.symbolThresholdList &&
                    `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[stock.symbolThresholdList.length - 1].toLocaleString() : "-"}`}
                </Box>
              </TableCellSmall>
              <TableCellSmall>{stockVisibleTypeLabel[stock.stockVisibleType] ?? '-'}</TableCellSmall>
              {/* <TableCellSmall>
                <Checkbox checked={stock.isDiscontinued} disabled />
              </TableCellSmall> */}
            </>
          )}
        </>
      ),
    [editableAmountCountCell, editableArrivalDateCell, isSupplier, selectedCenterWarehouseIndex, selectedRegionWarehouseIndex],
  );

  // 単品のセル
  const clickableSingleCell = useCallback(
    (
      stock: SupplierStockSingle | SupplierStockSingleInSet,
      show: boolean | undefined,
      index?: number,
    ) =>
      show ? (
        <TableRow key={stock.id}>
          <TableCellNarrow sx={{ textAlign: "center" }}>
            {stock.isDiscontinued ? (
              <CustomTooltip title="*がついているものは廃番です。" arrow>
                <Box sx={{ cursor: "pointer" }}>
                  <Typography component="span" sx={{ color: "var(--red-800)" }}>
                    *
                  </Typography>
                  {stock.productCd}
                </Box>
              </CustomTooltip>
            ) : (
              stock.productCd
            )}
          </TableCellNarrow>
          <TableCellSmall>{stock.JANCd}</TableCellSmall>
          <TableCellSmall>
            {classificationOptions.filter((c) => c.id === stock.classification).length > 0
              ? classificationOptions.filter((c) => c.id === stock.classification)[0].name
              : stock.classification}
          </TableCellSmall>
          <TableCellSmall>{stock.brand}</TableCellSmall>
          <TableCellWide>{stock.productName}</TableCellWide>
          <TableCellSmall>{stock.productNumber}</TableCellSmall>
          <TableCellSmall>{stock.size ?? null}</TableCellSmall>
          {isReadonly && readOnlyTBodyRender(stock)}
          {!isReadonly && editableTBodyRender(stock, index)}
        </TableRow>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      ),
    [
      checkProductSingleInSet,
      readOnlyTBodyRender,
      editableTBodyRender,
      isReadonly,
      classificationOptions,
    ],
  );

  // 混合のセル
  const fieldCell = useCallback(
    (stock: SupplierStock, index: number) => {
      let ele: JSX.Element;
      if (checkProductSet(stock)) {
        // セット
        ele = (
          <>
            <TableRow key={stock.id}>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                <Box display="flex" alignItems="center">
                  {stock.productSetCd}
                </Box>
              </TableCellSmall>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                <Box display="flex" alignItems="center">
                  {stock.JANCd}
                </Box>
              </TableCellSmall>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                {stock.classification}
              </TableCellSmall>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)} />
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                {}
              </TableCellSmall>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                <Box display="flex" alignItems="center">
                  {stock.productNumber}
                </Box>
              </TableCellSmall>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)} />
              {isReadonly && (
                <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                  {stock.amountSymbol ?? stock.amountCount}
                </TableCellSmall>
              )}
              {!isReadonly && (
                <>
                  <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                    {stock.amountSymbol}
                  </TableCellSmall>
                  <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                    {stock.amountCount}
                  </TableCellSmall>
                </>
              )}
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                {stock.arrivalDate}
              </TableCellSmall>
              <TableCellSmall>
                <Box textAlign="right">
                  {stock.symbolThresholdList &&
                    `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[0] : ""}`}
                </Box>
              </TableCellSmall>
              <TableCellSmall>
                <Box textAlign="right">
                  {stock.symbolThresholdList &&
                    `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[stock.symbolThresholdList.length - 1] : ""}`}
                </Box>
              </TableCellSmall>
              {/* <TableCellSmall>{renderRadioGroup(stockVisibleTypeLabel.map(s => s), stock)}</TableCellSmall> */}
            </TableRow>
            {stock.stockSingleList.map((pSet) => clickableSingleCell(pSet, stock.show))}
          </>
        );
      } else {
        // 単品
        ele = clickableSingleCell(stock, true, index);
      }
      return ele;
    },
    [checkProductSet, handleToggleOpenRow, clickableSingleCell, isReadonly],
  );

  const tableHeadElement = useCallback(
    () => (
      <>
        <TableRow>
          <TableHeaderCellNarrow rowSpan={2} sx={{ width: 30 }}>
            {isSupplier ? "YH商品コード" : "商品コード"}
          </TableHeaderCellNarrow>
          <TableHeaderCellSmall rowSpan={2}>JANコード</TableHeaderCellSmall>
          <TableHeaderCellSmall rowSpan={2}>大分類</TableHeaderCellSmall>
          <TableHeaderCellSmall rowSpan={2}>ブランド</TableHeaderCellSmall>
          <TableHeaderCellWide rowSpan={2}>商品名</TableHeaderCellWide>
          <TableHeaderCellSmall rowSpan={2}>品番</TableHeaderCellSmall>
          <TableHeaderCellSmall rowSpan={2}>サイズ</TableHeaderCellSmall>
          { isSupplier &&
            (Number(checkExistRegionWarehouse()) + Number(checkExistCenterWarehouse()) > 0) && <TableHeaderCellNarrow colSpan={Number(checkExistRegionWarehouse()) + Number(checkExistCenterWarehouse())} sx={{textAlign: "center"}}>ユーザー表示</TableHeaderCellNarrow>
          }
          { (isReadonly && !isSupplier) &&
            <TableHeaderCellNarrow rowSpan={2} sx={{textAlign: "center", maxWidth: 60}}>ユーザー表示</TableHeaderCellNarrow>
          }
          { !isReadonly && (
            isSupplier ?
              (Number(checkExistRegionWarehouse()) + Number(checkExistCenterWarehouse()) > 0) && <TableHeaderCellWide colSpan={Number(checkExistRegionWarehouse()) + Number(checkExistCenterWarehouse())} sx={{textAlign: "center"}}>在庫数量</TableHeaderCellWide> :
              <TableHeaderCellWide rowSpan={2}>在庫数量</TableHeaderCellWide>
            )
          }
          <TableHeaderCellWide rowSpan={2}>入荷予定日</TableHeaderCellWide>
          {isSupplier && (
            <>
              <TableHeaderCellSmall colSpan={2} rowSpan={1} sx={styles.supplierTopCol}>
                在庫数設定値
                <CustomTooltip
                  title={
                    <>
                      ○~△境界値：在庫数量が境界値以上になると、ユーザー表示が○に、未満だと△に変わります(在庫数量は△~×境界値以上である前提)。
                      <br />
                      △~×境界値：在庫数量が境界値以上になると、ユーザー表示が△に、未満だと×に変わります(在庫数量は○~△境界値未満である前提)。
                    </>
                  }
                  arrow
                >
                  <Box sx={{ cursor: "pointer" }}>
                    <Typography
                      component="span"
                      sx={{ color: "var(--red-600)", position: "absolute", top: 0, right: 12 }}
                    >
                      <HelpIcon sx={{ fontSize: 18 }} />
                    </Typography>
                  </Box>
                </CustomTooltip>
              </TableHeaderCellSmall>
              <TableHeaderCellSmall rowSpan={2}>在庫数表示</TableHeaderCellSmall>
              {/* {!isReadonly && <TableHeaderCellSmall rowSpan={2}>廃番</TableHeaderCellSmall>} */}
            </>
          )}
        </TableRow>
        {isSupplier && (
          <TableRow>
            {/* { stockList && stockList.length > 0 && stockList[0].supplierWarehouses?.map((sw) =>
              <TableHeaderCellNarrow sx={{...styles.supplierBottomCol, ...{textAlign: "center"}}}>{sw.name}<br/>倉庫</TableHeaderCellNarrow>
            )}
            <TableHeaderCellNarrow sx={{...styles.supplierBottomCol, ...{textAlign: "center"}}}>センター倉庫</TableHeaderCellNarrow> */}
            <>
              {/* {stockList && stockList.length > 0 && stockList[0].supplierWarehouses?.map((sw) => */}
              { stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => sw.isCenter).length > 0 && (() => {
                // センター倉庫がある場合表示
                const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => sw.isCenter);
                return (
                  <TableHeaderCellWide sx={{...styles.supplierBottomCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
                    <SelectForm
                      id="center_warehouse"
                      label="センター倉庫"
                      optionList={supplierWarehouses.map(sw => sw.name)}
                      value={supplierWarehouses[selectedCenterWarehouseIndex].name}
                      setValue={(val: string) => setSelectedCenterWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
                      width={160}
                    />
                  </TableHeaderCellWide>
                )
              })()}
              { stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter).length > 0 && (() => {
                // 地域倉庫がある場合表示
                const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter);
                return (
                  <TableHeaderCellWide sx={{...styles.supplierBottomCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
                    <SelectForm
                      id="region_warehouse"
                      label="地域倉庫"
                      optionList={supplierWarehouses.map(sw => sw.name)}
                      value={supplierWarehouses[selectedRegionWarehouseIndex].name}
                      setValue={(val: string) => setSelectedRegionWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
                      width={160}
                    />
                  </TableHeaderCellWide>
                )
              })()}
            </>
            { !isReadonly &&
              <>
                { stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => sw.isCenter).length > 0 && (() => (
                  <TableHeaderCellNarrow sx={{...styles.supplierBottomCol, ...{textAlign: "center"}}}>
                    {(() => {
                      // センター倉庫がある場合表示
                      const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => sw.isCenter);
                      return supplierWarehouses[selectedCenterWarehouseIndex].name;
                    })()}
                  </TableHeaderCellNarrow>
                ))()}
                { stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter).length > 0 && (() => (
                  <TableHeaderCellNarrow sx={{...styles.supplierBottomCol, ...{textAlign: "center"}}}>
                    {(() => {
                      // 地域倉庫がある場合表示
                      const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter);
                      return supplierWarehouses[selectedRegionWarehouseIndex].name;
                    })()}
                  </TableHeaderCellNarrow>
                ))()}
              </>
            }
            <TableHeaderCellSmall sx={styles.supplierBottomCol}>
              <MultiTypography sx={{ textAlign: "center" }}>
                <>
                  ○~△
                  <br />
                  境界値
                </>
              </MultiTypography>
            </TableHeaderCellSmall>
            <TableHeaderCellSmall sx={styles.supplierBottomCol}>
              <MultiTypography sx={{ textAlign: "center" }}>
                <>
                  △~×
                  <br />
                  境界値
                </>
              </MultiTypography>
            </TableHeaderCellSmall>
          </TableRow>
        )}
      </>
    ),
    [isSupplier, isReadonly, checkExistRegionWarehouse, checkExistCenterWarehouse, stockList, selectedCenterWarehouseIndex, selectedRegionWarehouseIndex],
  );

  const tableBodyElement = useCallback(
    () => (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>{stockList.length > 0 && stockList.map((stock, index) => fieldCell(stock, index))}</>
    ),
    [stockList, fieldCell],
  );

  const selectHeaderCenterWarehouse = () => {
    if (stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => sw.isCenter).length > 0) {
      const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => sw.isCenter);
      return (
        <TableHeaderCellWide sx={{...styles.supplierTopCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
          <SelectForm
            id="center_warehouse"
            label="センター倉庫"
            optionList={supplierWarehouses.map(sw => sw.name)}
            value={supplierWarehouses[selectedCenterWarehouseIndex].name}
            setValue={(val: string) => setSelectedCenterWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
            width={160}
          />
        </TableHeaderCellWide>
      )
    }
    return <Box/>;
  };

  const selectHeaderRegionWarehouse = () => {
    if (stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter).length > 0) {
      // 地域倉庫がある場合表示
      const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter);
      return (
        <TableHeaderCellWide sx={{...styles.supplierTopCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
          <SelectForm
            id="region_warehouse"
            label="地域倉庫"
            optionList={supplierWarehouses.map(sw => sw.name)}
            value={supplierWarehouses[selectedRegionWarehouseIndex].name}
            setValue={(val: string) => setSelectedRegionWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
            width={160}
          />
        </TableHeaderCellWide>
      )
    }
    return <Box/>;
  };

  // 現在選択されているセンター倉庫数量
  const bodyCenterWarehouse = (id: string) => {
    const stock: SupplierStockSingle | undefined = stockList.find(s => s.id === id) as SupplierStockSingle;
    return stock && isSupplier && stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => sw.isCenter).length > 0
      ? <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => sw.isCenter)[selectedCenterWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
      : <Box/>
  };
  // 現在選択されている地域倉庫数量
  const bodyRegionWarehouse = (id: string) => {
    const stock: SupplierStockSingle | undefined = stockList.find(s => s.id === id) as SupplierStockSingle;
    return stock && isSupplier && stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => !sw.isCenter).length > 0
      ? <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => !sw.isCenter)[selectedRegionWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
      : <Box/>
  };

  // スクロール処理
  const handleScroll = async () => {
    setIsScrollTop(window.pageYOffset > 0);

    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current) {
      return;
    }
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  const checkScrollable = () => document.documentElement.scrollHeight > window.innerHeight;

  // 中のコンテンツが小さいとスクロールできず、無限ローディングさせることができないため、事前に+αで読んでおく。
  useEffect(() => {
    if (limit <= stockList.length && stockList.length < total && !isLoading && !isFastLoading.current) {
      if (!checkScrollable()) {
        (async() => {
          // 仕入先の場合は仕入先コードをfillするために取得
          await getMoreStockList(createGetListApiParams(), isSupplier);
        })();
      }
    }
  }, [stockList, isLoading]);

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      // 仕入先の場合は仕入先コードをfillするために取得
      await getMoreStockList(createGetListApiParams(), isSupplier);
    })();
  }, [fireMoreLoadEvent]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    setTimeout(() => {
      (async () => {
        if (!isExistCd) {
          setIsOpenStockSearch(false);
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: "仕入先または倉庫コードが設定されていません。再度ログインしてください。",
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else if (checkAccessTokenExpired()) {
          // 期限切れなら
          setIsOpenStockSearch(false);
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else {
          // 仕入先の場合は仕入先コードをfillするために取得
          await getStockList({ code: supplierCd }, isSupplier);
          setInit(true);
        }
      })();
    }, 0);
  }, []);
  useEffect(() => {
    // loadingの統一
    setIsLoading(isOptionsLoading || isStockLoading);
  }, [isOptionsLoading, isStockLoading]);

  return (
    <Box>
      <StockContainer
        stockTitle="在庫情報一覧"
        StockSearch={
          <StockSearchDialog
            open={isOpenStockSearch}
            onClose={() => setIsOpenStockSearch(false)}
            onSearch={onSearchClick}
            productCd={productCd}
            productName={productName}
            brand={brand}
            JANCd={JANCd}
            productNumber={productNumber}
            tyreWidth={tyreWidth}
            tyreRatio={tyreRatio}
            tyreRimDiam={tyreRimDiam}
            wheelWidth={wheelWidth}
            wheelPcd={wheelPcd}
            wheelHole={wheelHole}
            wheelRimDiam={wheelRimDiam}
            classification={classification}
            classificationOptions={classificationOptions}
            tyreOptions={tyreOptions}
            wheelOptions={wheelOptions}
            brandOptions={brandOptions}
            setProductCd={setProductCd}
            setProductName={setProductName}
            setBrand={setBrand}
            setJANCd={setJANCd}
            setProductNumber={setProductNumber}
            setTyreWidth={setTyreWidth}
            setTyreRatio={setTyreRatio}
            setTyreRimDiam={setTyreRimDiam}
            setWheelWidth={setWheelWidth}
            setWheelPcd={setWheelPcd}
            setWheelHole={setWheelHole}
            setWheelRimDiam={setWheelRimDiam}
            setClassification={setClassification}
            setCategory={setCategory}
            isSupplierStockScreen
          />
        }
        StockTable={
          <StockTable
            searchLen={total}
            tableHeadElement={tableHeadElement}
            tableBodyElement={tableBodyElement}
            isAllLoaded={stockList.length >= total}
            isLoading={isMoreStockLoading}
            supplierCd={supplierCd}
            isSupplier={isSupplier}
          />
        }
        setIsOpenStockSearch={setIsOpenStockSearch}
        isLoading={isLoading}
        onUpdate={() => onUpdateConfirm()}
        changedStockList={changedStockList}
        isReadonly={isReadonly}
        onChangeReadonlyScreen={onChangeReadonlyScreen}
        setIsOpenStockCsvUpload={setIsOpenStockCsvUpload}
        setIsOpenThresholdCsvUpload={setIsOpenThresholdCsvUpload}
        supplierCd={storehouseSupplier === storehouseSupplierOptions[1] ? supplierCd : undefined}
        storehouseCd={storehouseSupplier === storehouseSupplierOptions[0] ? supplierCd : undefined}
        csvDownloading={csvDownloading}
        onBack={handleBack}
        manualName={`manual_${getUserType() === "C0004" ? "supplier" : "depot"}`}
      />
      <StockUpdateConfirmDialog
        open={isOpenUpdateConfirmDialog}
        onClose={setIsOpenUpdateConfirmDialog}
        onUpdate={onUpdateAmount}
        changedStockList={changedStockList}
        isUpdateProcessing={isUpdateProcessing}
        isSupplier={isSupplier}
        selectHeaderRegionWarehouse={selectHeaderRegionWarehouse} // 近隣倉庫があればSelectbox要素が入る
        selectHeaderCenterWarehouse={selectHeaderCenterWarehouse} // センター倉庫があればSelectbox要素が入る
        bodyCenterWarehouse={bodyCenterWarehouse}
        bodyRegionWarehouse={bodyRegionWarehouse}
      />
      <CsvModals
        isOpenStockCsvUpload={isOpenStockCsvUpload}
        setIsOpenStockCsvUpload={setIsOpenStockCsvUpload}
        refresh={() => getStockListUpdate()}
        isOpenThresholdCsvUpload={isOpenThresholdCsvUpload}
        setIsOpenThresholdCsvUpload={setIsOpenThresholdCsvUpload}
        supplierCd={storehouseSupplier === storehouseSupplierOptions[1] ? supplierCd : undefined}
        storehouseCd={storehouseSupplier === storehouseSupplierOptions[0] ? supplierCd : undefined}
      />
      {isScrollTop &&
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <SpeedDial
            ariaLabel="Back to top"
            icon={<KeyboardArrowUpIcon />}
            onClick={scrollToTop}
            open             // 常に展開済みにしてアイコンだけ表示
            direction="up"          // 展開方向は上だがActionは不要
            FabProps={{ size: 'small' }}
            />
        </Box>
      }
    </Box>
  );
}
